import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Loader2, RefreshCw, Sparkles } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ScrollArea } from '../ui/scroll-area';
import { EnhancedChatMessage } from './EnhancedChatMessage';
import { cn } from '../../lib/utils';

interface ChatMessage {
  id: string;
  message: string;
  reply?: string;
  timestamp: string;
  agent_type?: string;
  confidence?: number;
  suggestedFollowups?: string[];
  sources?: any[];
  isLoading?: boolean;
}

interface ChatWidgetProps {
  apiEndpoint: string;
  siteId: string;
  sessionId: string;
  visitorId: string;
  pageContent?: string;
  pageUrl?: string;
  className?: string;
  position?: 'bottom-right' | 'bottom-left' | 'embedded';
  theme?: 'light' | 'dark' | 'auto';
  welcomeMessage?: string;
  placeholder?: string;
}

export function ChatWidget({
  apiEndpoint,
  siteId,
  sessionId,
  visitorId,
  pageContent = '',
  pageUrl = window.location.href,
  className,
  position = 'bottom-right',
  theme = 'auto',
  welcomeMessage = "Hi! I'm here to help. Ask me anything about this page!",
  placeholder = "Type your message..."
}: ChatWidgetProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: 'welcome',
      message: '',
      reply: welcomeMessage,
      timestamp: new Date().toISOString(),
      agent_type: 'assistant'
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  
  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (scrollAreaRef.current) {
      scrollAreaRef.current.scrollTop = scrollAreaRef.current.scrollHeight;
    }
  }, [messages]);
  
  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);
  
  const sendMessage = async (messageText: string) => {
    if (!messageText.trim() || isLoading) return;
    
    const userMessage: ChatMessage = {
      id: `msg-${Date.now()}`,
      message: messageText,
      timestamp: new Date().toISOString()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    
    // Add loading message
    const loadingMessage: ChatMessage = {
      id: `loading-${Date.now()}`,
      message: '',
      timestamp: new Date().toISOString(),
      isLoading: true
    };
    setMessages(prev => [...prev, loadingMessage]);
    
    try {
      const response = await fetch(apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: messageText,
          siteId,
          sessionId,
          visitorId,
          pageContent,
          url: pageUrl
        })
      });
      
      const data = await response.json();
      
      // Remove loading message and add real response
      setMessages(prev => {
        const filtered = prev.filter(msg => !msg.isLoading);
        return [...filtered.slice(0, -1), {
          ...userMessage,
          reply: data.text || data.reply || "I'm sorry, I couldn't process your request.",
          agent_type: data.agent_type || 'assistant',
          confidence: data.confidence,
          suggestedFollowups: data.suggestedFollowups,
          sources: data.sources
        }];
      });
    } catch (error) {
      console.error('Chat error:', error);
      // Remove loading message and add error response
      setMessages(prev => {
        const filtered = prev.filter(msg => !msg.isLoading);
        return [...filtered.slice(0, -1), {
          ...userMessage,
          reply: "I'm having trouble connecting right now. Please try again in a moment.",
          agent_type: 'error'
        }];
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleFollowupClick = (question: string) => {
    sendMessage(question);
  };
  
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };
  
  const positionClasses = {
    'bottom-right': 'fixed bottom-4 right-4 z-50',
    'bottom-left': 'fixed bottom-4 left-4 z-50',
    'embedded': 'relative w-full h-full'
  };
  
  const widgetClasses = cn(
    positionClasses[position],
    position !== 'embedded' && 'transition-all duration-300',
    className
  );
  
  if (position === 'embedded') {
    return (
      <div className={cn("flex flex-col bg-white dark:bg-gray-900 rounded-lg shadow-xl", widgetClasses)}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
              <Sparkles className="h-4 w-4 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold text-sm">AI Assistant</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">Always here to help</p>
            </div>
          </div>
        </div>
        
        {/* Messages */}
        <ScrollArea className="flex-1 p-4" ref={scrollAreaRef}>
          <div className="space-y-4">
            {messages.map((msg) => (
              msg.isLoading ? (
                <div key={msg.id} className="flex items-center gap-2 text-gray-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm">Thinking...</span>
                </div>
              ) : (
                <EnhancedChatMessage
                  key={msg.id}
                  message={msg}
                  onFollowupClick={handleFollowupClick}
                />
              )
            ))}
          </div>
        </ScrollArea>
        
        {/* Input */}
        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex gap-2">
            <Input
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={placeholder}
              disabled={isLoading}
              className="flex-1"
            />
            <Button
              onClick={() => sendMessage(input)}
              disabled={!input.trim() || isLoading}
              size="icon"
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  }
  
  // Floating widget
  return (
    <div className={widgetClasses}>
      {/* Chat Window */}
      {isOpen && (
        <div className={cn(
          "absolute bottom-16 w-96 h-[600px] bg-white dark:bg-gray-900 rounded-lg shadow-2xl",
          "transform transition-all duration-300 origin-bottom-right",
          "animate-in slide-in-from-bottom-5 fade-in-0",
          position === 'bottom-left' && "origin-bottom-left right-auto left-0"
        )}>
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 rounded-t-lg">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                <Sparkles className="h-4 w-4 text-primary animate-pulse" />
              </div>
              <div>
                <h3 className="font-semibold text-sm">AI Assistant</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">Always here to help</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => setIsOpen(false)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          
          {/* Messages */}
          <ScrollArea className="h-[calc(100%-8rem)] p-4" ref={scrollAreaRef}>
            <div className="space-y-4">
              {messages.map((msg) => (
                msg.isLoading ? (
                  <div key={msg.id} className="flex items-center gap-2 text-gray-500">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-sm italic">Thinking...</span>
                  </div>
                ) : (
                  <EnhancedChatMessage
                    key={msg.id}
                    message={msg}
                    onFollowupClick={handleFollowupClick}
                  />
                )
              ))}
            </div>
          </ScrollArea>
          
          {/* Input */}
          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-200 dark:border-gray-700 rounded-b-lg bg-white dark:bg-gray-900">
            <div className="flex gap-2">
              <Input
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={placeholder}
                disabled={isLoading}
                className="flex-1"
              />
              <Button
                onClick={() => sendMessage(input)}
                disabled={!input.trim() || isLoading}
                size="icon"
                className="shrink-0"
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
      
      {/* Toggle Button */}
      <Button
        onClick={() => setIsOpen(!isOpen)}
        size="icon"
        className={cn(
          "h-14 w-14 rounded-full shadow-lg",
          "hover:scale-110 transition-transform",
          isOpen && "scale-110"
        )}
      >
        {isOpen ? (
          <X className="h-6 w-6" />
        ) : (
          <MessageCircle className="h-6 w-6" />
        )}
      </Button>
      
      {/* Notification Badge */}
      {!isOpen && messages.length > 1 && (
        <div className="absolute -top-1 -right-1 h-3 w-3 bg-red-500 rounded-full animate-pulse" />
      )}
    </div>
  );
}